import { computed, ref, watch } from 'vue'

const STORAGE_KEY = 'beiyidata-locale'
const SUPPORTED_LOCALES = ['zh', 'en', 'es', 'fr', 'ja']

const localeNames = {
  zh: '中文',
  en: 'EN',
  es: 'ES',
  fr: 'FR',
  ja: 'JP',
}

const seoContent = {
  title: {
    zh: '贝易数据｜智能数据治理、文档处理与企业决策',
    en: 'Beiyi Data · Data Governance, Document Processing & Enterprise Decisions',
    es: 'Beiyi Data · Gobierno de datos, Documentos y Decisiones empresariales',
    fr: "Beiyi Data · Gouvernance data, Documents et Décisions d'entreprise",
    ja: 'Beiyi Data · データガバナンス・文書処理・企業意思決定',
  },
  description: {
    zh: '武汉贝易科技提供智能数据治理平台、智能文档处理平台、NEXA Decision OS 与智能知识库，支持外部数据开发引擎、自定义智能体、私有化部署与 SaaS 租户服务。',
    en: 'Enterprise data governance, intelligent document processing, NEXA Decision OS, knowledge management, with external engines, custom agents, private deployment and SaaS tenant services.',
    es: 'Gobierno de datos, procesamiento documental, NEXA y gestión del conocimiento para empresas.',
    fr: "Gouvernance des données, traitement documentaire, NEXA et gestion des connaissances pour les entreprises.",
    ja: '企業向けデータガバナンス、文書処理、NEXA、ナレッジ管理を提供する Beiyi Data の公式サイト。',
  },
}

const detectLocale = () => {
  if (typeof window === 'undefined') {
    return 'zh'
  }

  const saved = window.localStorage.getItem(STORAGE_KEY)
  if (SUPPORTED_LOCALES.includes(saved)) {
    return saved
  }

  const lang = window.navigator.language.toLowerCase()
  if (lang.startsWith('zh')) return 'zh'
  if (lang.startsWith('es')) return 'es'
  if (lang.startsWith('fr')) return 'fr'
  if (lang.startsWith('ja')) return 'ja'
  return 'en'
}

const locale = ref(detectLocale())

const resolveText = (value, currentLocale) => {
  if (typeof value === 'string') return value
  if (!value || typeof value !== 'object') return ''

  return (
    value[currentLocale]
    ?? value.en
    ?? value.zh
    ?? value.es
    ?? value.fr
    ?? value.ja
    ?? ''
  )
}

const resolveList = (value, currentLocale) => {
  if (Array.isArray(value)) return value
  if (!value || typeof value !== 'object') return []

  return (
    value[currentLocale]
    ?? value.en
    ?? value.zh
    ?? value.es
    ?? value.fr
    ?? value.ja
    ?? []
  )
}

watch(
  locale,
  (next) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, next)
    }

    if (typeof document !== 'undefined') {
      const langMap = {
        zh: 'zh-CN',
        en: 'en',
        es: 'es',
        fr: 'fr',
        ja: 'ja',
      }
      document.documentElement.lang = langMap[next] ?? 'en'
      document.title = seoContent.title[next] ?? seoContent.title.en

      const descriptionTag = document.querySelector('meta[name="description"]')
      if (descriptionTag) {
        descriptionTag.setAttribute('content', seoContent.description[next] ?? seoContent.description.en)
      }
    }
  },
  { immediate: true },
)

export const useI18n = () => {
  const t = (value) => resolveText(value, locale.value)
  const ta = (value) => resolveList(value, locale.value)

  const setLocale = (nextLocale) => {
    if (!SUPPORTED_LOCALES.includes(nextLocale)) return
    locale.value = nextLocale
  }

  return {
    locale: computed(() => locale.value),
    locales: SUPPORTED_LOCALES,
    localeNames,
    setLocale,
    t,
    ta,
  }
}
